import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'

const SubscriptionContext = createContext()

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)

  // Premium features list
  const premiumFeatures = {
    allPaths: 'Access to all disaster paths',
    aiSearch: 'AI-powered intelligent search',
    familySync: 'Family sync & competitions',
    offlineMode: 'Download lessons for offline access',
    unlimitedChaos: 'Unlimited Chaos Mode alerts',
    certificates: 'Completion certificates',
    adFree: 'Ad-free experience',
    printables: 'Printable checklists & guides'
  }

  // Free tier limits
  const freeLimits = {
    paths: 1, // Only 1 path unlocked
    chaosAlertsPerDay: 3,
    aiSearchPerDay: 0, // No AI search
  }

  useEffect(() => {
    if (user) {
      loadSubscription()
    } else {
      setSubscription(null)
      setLoading(false)
    }
  }, [user])

  const loadSubscription = async () => {
    try {
      // Fetch subscription status from API
      const apiUrl = import.meta.env.VITE_API_URL || 'https://surviveitapp.com/api'
      const response = await fetch(`${apiUrl}/v1/stripe/status/${user.id}`)
      const data = await response.json()

      if (data.isPremium) {
        setSubscription({
          status: 'active',
          plan: data.plan,
          expiresAt: data.expiresAt,
          stripeCustomerId: data.stripeCustomerId
        })
      } else {
        // Fallback to localStorage for mock testing
        const stored = localStorage.getItem('surviveit_subscription')
        if (stored) {
          const sub = JSON.parse(stored)
          if (sub.expiresAt && new Date(sub.expiresAt) > new Date()) {
            setSubscription(sub)
          } else {
            setSubscription(null)
            localStorage.removeItem('surviveit_subscription')
          }
        } else {
          setSubscription(null)
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error)
      // Fallback to localStorage on error
      const stored = localStorage.getItem('surviveit_subscription')
      if (stored) {
        setSubscription(JSON.parse(stored))
      } else {
        setSubscription(null)
      }
    } finally {
      setLoading(false)
    }
  }

  const isPremium = () => {
    return subscription?.status === 'active'
  }

  const canAccessFeature = (feature) => {
    if (isPremium()) return true

    // Free tier access
    const freeFeatures = ['emergencyMode', 'personalityQuiz', 'dailyTips', 'basicProgress']
    return freeFeatures.includes(feature)
  }

  const canAccessPath = (pathIndex) => {
    if (isPremium()) return true
    // Free users can only access first path (Nuclear Attack)
    return pathIndex === 0
  }

  const getRemainingChaosAlerts = () => {
    if (isPremium()) return Infinity

    const today = new Date().toDateString()
    const alertsToday = JSON.parse(localStorage.getItem('chaos_alerts_today') || '{}')

    if (alertsToday.date !== today) {
      return freeLimits.chaosAlertsPerDay
    }

    return Math.max(0, freeLimits.chaosAlertsPerDay - (alertsToday.count || 0))
  }

  const recordChaosAlert = () => {
    const today = new Date().toDateString()
    const alertsToday = JSON.parse(localStorage.getItem('chaos_alerts_today') || '{}')

    if (alertsToday.date !== today) {
      localStorage.setItem('chaos_alerts_today', JSON.stringify({ date: today, count: 1 }))
    } else {
      alertsToday.count = (alertsToday.count || 0) + 1
      localStorage.setItem('chaos_alerts_today', JSON.stringify(alertsToday))
    }
  }

  // Mock subscription for testing (will be replaced with Stripe)
  const mockSubscribe = async (plan) => {
    const now = new Date()
    const expiresAt = plan === 'yearly'
      ? new Date(now.setFullYear(now.getFullYear() + 1))
      : new Date(now.setMonth(now.getMonth() + 1))

    const newSubscription = {
      status: 'active',
      plan: plan,
      startedAt: new Date().toISOString(),
      expiresAt: expiresAt.toISOString(),
      features: Object.keys(premiumFeatures)
    }

    localStorage.setItem('surviveit_subscription', JSON.stringify(newSubscription))
    setSubscription(newSubscription)
    return newSubscription
  }

  const cancelSubscription = async () => {
    localStorage.removeItem('surviveit_subscription')
    setSubscription(null)
  }

  const value = {
    subscription,
    loading,
    isPremium,
    canAccessFeature,
    canAccessPath,
    premiumFeatures,
    freeLimits,
    getRemainingChaosAlerts,
    recordChaosAlert,
    mockSubscribe,
    cancelSubscription
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export default SubscriptionContext
